// src/components/facturacion/ModalCobros.js
// =========================================================================
// 💶 COBROS DE UNA FACTURA
// Una factura se puede cobrar en varias veces y por varios métodos (señal y
// resto, parte en efectivo y parte con tarjeta…). Aquí se ve lo pagado y lo
// pendiente, se registra un cobro y dirección puede anular uno erróneo
// (queda constancia: nunca se borra).
// =========================================================================
import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import api from '../../services/api';
import { Modal } from '../common/ui';
import { euros } from '../../utils/formatos';

const METODOS = [['efectivo', 'Efectivo'], ['tarjeta', 'Tarjeta'], ['transferencia', 'Transferencia']];
const entrada = 'w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:border-clinica-primary focus:ring-2 focus:ring-clinica-primary/15';

export default function ModalCobros({ factura, onClose, onCambio, puedeAnular }) {
    const [actual, setActual] = useState(factura);
    const [importe, setImporte] = useState('');
    const [metodo, setMetodo] = useState(factura?.metodoPago || 'tarjeta');
    const [nota, setNota] = useState('');
    const [error, setError] = useState('');
    const [guardando, setGuardando] = useState(false);
    const [anulando, setAnulando] = useState(null); // { id, motivo }

    useEffect(() => {
        setActual(factura);
        setImporte(factura?.pendiente ? String(factura.pendiente) : '');
        setMetodo(factura?.metodoPago || 'tarjeta');
        setNota('');
        setError('');
        setAnulando(null);
    }, [factura]);

    if (!actual) return null;
    const pendiente = Number(actual.pendiente ?? 0);
    const anulada = actual.estado === 'anulada';

    const aplicar = (dto) => {
        setActual(dto);
        setImporte(dto.pendiente ? String(dto.pendiente) : '');
        onCambio?.(dto);
    };

    const cobrar = async (e) => {
        e.preventDefault();
        const cantidad = Number(String(importe).replace(',', '.'));
        if (!(cantidad > 0)) return setError('Indica un importe mayor que cero.');
        if (cantidad > pendiente + 0.005) return setError(`No puede superar lo pendiente (${euros(pendiente)}).`);
        setError('');
        setGuardando(true);
        try {
            aplicar(await api.post(`/facturas/${actual.id}/pagos`, { importe: cantidad, metodo, nota: nota.trim() }));
            setNota('');
        } catch (err) {
            setError(err.message || 'No se pudo registrar el cobro.');
        } finally {
            setGuardando(false);
        }
    };

    const anular = async () => {
        if (!anulando?.motivo?.trim()) return setError('Indica el motivo de la anulación.');
        setError('');
        setGuardando(true);
        try {
            aplicar(await api.delete(`/facturas/${actual.id}/pagos/${anulando.id}`, { data: { motivo: anulando.motivo.trim() } }));
            setAnulando(null);
        } catch (err) {
            setError(err.message || 'No se pudo anular el cobro.');
        } finally {
            setGuardando(false);
        }
    };

    const pagos = actual.pagos || [];

    return (
        <Modal open onClose={onClose} titulo={`Cobros · ${actual.numeroFactura || 'Factura'}`}
               subtitulo={actual.paciente ? `${actual.paciente.nombre} ${actual.paciente.apellidos}` : undefined} size="lg">
            <div className="space-y-6">
                <div className="grid grid-cols-3 gap-3">
                    {[['Total', actual.total, 'text-slate-900'], ['Pagado', actual.pagado, 'text-emerald-700'], ['Pendiente', pendiente, pendiente > 0 ? 'text-amber-700' : 'text-slate-400']].map(([t, v, c]) => (
                        <div key={t} className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">{t}</div>
                            <div className={`text-xl font-black mt-1 ${c}`}>{euros(v)}</div>
                        </div>
                    ))}
                </div>

                <div>
                    <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Pagos</h3>
                    {pagos.length === 0 ? (
                        <p className="text-sm font-semibold text-slate-400">
                            {actual.estado === 'pagada' ? `Cobrada en un solo pago (${actual.metodoPago}).` : 'Todavía no hay ningún cobro.'}
                        </p>
                    ) : (
                        <ul className="divide-y divide-slate-100 border border-slate-100 rounded-2xl">
                            {pagos.map(p => (
                                <li key={p.id} className={`p-3 text-sm ${p.anulado ? 'opacity-60' : ''}`}>
                                    <div className="flex items-center gap-3">
                                        <span className="w-28 shrink-0 text-xs font-semibold text-slate-500">{p.fecha ? format(new Date(p.fecha), 'dd/MM/yyyy HH:mm') : '—'}</span>
                                        <span className="w-28 shrink-0 font-bold text-slate-700 capitalize">{p.metodo}</span>
                                        <span className={`flex-1 min-w-0 truncate text-xs text-slate-500 ${p.anulado ? 'line-through' : ''}`}>{p.nota}</span>
                                        <span className={`font-black ${p.anulado ? 'line-through text-slate-400' : 'text-slate-900'}`}>{euros(p.importe)}</span>
                                        {p.anulado ? (
                                            <span className="text-[10px] font-black uppercase text-rose-600" title={p.motivoAnulacion}>Anulado</span>
                                        ) : puedeAnular && !anulada && (
                                            <button type="button" onClick={() => setAnulando({ id: p.id, motivo: '' })}
                                                    className="text-xs font-bold text-rose-600 hover:underline">Anular</button>
                                        )}
                                    </div>
                                    {anulando?.id === p.id && (
                                        <div className="mt-2 flex gap-2">
                                            <input value={anulando.motivo} onChange={e => setAnulando(a => ({ ...a, motivo: e.target.value }))}
                                                   placeholder="Motivo de la anulación" aria-label="Motivo de la anulación" className={entrada} />
                                            <button type="button" onClick={anular} disabled={guardando}
                                                    className="shrink-0 px-3 rounded-xl text-xs font-black text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50">Anular cobro</button>
                                            <button type="button" onClick={() => setAnulando(null)}
                                                    className="shrink-0 px-3 rounded-xl text-xs font-bold text-slate-600 bg-slate-100 hover:bg-slate-200">Cancelar</button>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {!anulada && pendiente > 0 && (
                    <form onSubmit={cobrar} noValidate className="space-y-3 p-4 rounded-2xl border border-slate-200">
                        <h3 className="text-xs font-black uppercase tracking-widest text-slate-400">Registrar cobro</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                                <label htmlFor="cobro-importe" className="block mb-1 text-xs font-bold text-slate-600">Importe (€)</label>
                                <input id="cobro-importe" type="number" min="0.01" step="0.01" value={importe} onChange={e => setImporte(e.target.value)} className={entrada} />
                            </div>
                            <div>
                                <label htmlFor="cobro-metodo" className="block mb-1 text-xs font-bold text-slate-600">Método</label>
                                <select id="cobro-metodo" value={metodo} onChange={e => setMetodo(e.target.value)} className={entrada}>
                                    {METODOS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="cobro-nota" className="block mb-1 text-xs font-bold text-slate-600">Nota (opcional)</label>
                                <input id="cobro-nota" value={nota} onChange={e => setNota(e.target.value)} maxLength={200} placeholder="Señal, resto…" className={entrada} />
                            </div>
                        </div>
                        <div className="flex justify-end">
                            <button type="submit" disabled={guardando}
                                    className="px-5 py-2.5 rounded-xl text-sm font-black text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50">
                                {guardando ? 'Guardando…' : 'Registrar cobro'}
                            </button>
                        </div>
                    </form>
                )}

                {error && <p role="alert" className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-sm font-semibold text-rose-700">{error}</p>}
            </div>
        </Modal>
    );
}
